// ai-retention-platform.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "AI that predicts which clients will leave before they do. Smart automation that knows when to reach out. Retention intelligence that gets smarter over time.",

  problemSection: {
    title: "Why Traditional Retention Fails",
    html: `
      <p>Most retention approaches are reactive. You notice a client is gone when their renewal doesn't hit. You find out they were unhappy when they've already enrolled elsewhere. By the time you know there's a problem, it's too late to fix it.</p>

      <p>The agents who retain best don't wait for signals—they predict them. They know which clients are at risk before those clients start shopping. They reach out proactively, at the right time, with the right message.</p>

      <p>That's what AI makes possible. Not generic automation, but intelligent systems that analyze patterns, predict behavior, and guide your actions based on data—not guesses.</p>

      <p>With nearly half of new MA enrollees leaving within 5 years and 1 in 10 switching annually, the question isn't whether you need better retention tools. It's whether you can afford not to have them.</p>
    `
  },

  features: [
    {
      icon: "target",
      title: "Predictive Risk Scoring",
      description: "Machine learning analyzes dozens of factors to calculate churn probability for every client. Know who's at risk before they start shopping."
    },
    {
      icon: "zap",
      title: "Smart Timing",
      description: "AI determines the optimal time to reach out based on client patterns, plan changes, and historical data. Not random reminders—intelligent triggers."
    },
    {
      icon: "bell",
      title: "Proactive Alerts",
      description: "Get notified about risk factors as they emerge: plan changes, network disruptions, extended silence. Act before problems become departures."
    },
    {
      icon: "chart",
      title: "Pattern Recognition",
      description: "The system learns from your book's patterns. Which touchpoints work? Which clients are similar to those who've churned? AI finds insights you'd miss."
    },
    {
      icon: "users",
      title: "Personalized Recommendations",
      description: "AI suggests specific actions for each client based on their situation: what to say, when to call, what to address. Not generic advice—tailored guidance."
    },
    {
      icon: "shield",
      title: "Continuous Learning",
      description: "The system improves over time. Every outcome—retained or churned—trains the model. Your retention intelligence gets smarter the more you use it."
    }
  ],

  featuresTitle: "How AI Changes Retention",

  mainContent: `
    <h2>From Reactive to Predictive</h2>

    <p>Traditional retention is backward-looking. You analyze last year's churn and hope to do better. AI-powered retention is forward-looking. You predict this year's churn and prevent it.</p>

    <p>Here's the difference in practice:</p>

    <h3>Traditional Approach</h3>
    <ul>
      <li>Client has a problem → Problem escalates → Client shops around → Client leaves → You find out</li>
      <li>Time from problem to departure: Often weeks or months</li>
      <li>Intervention window: Usually zero (already gone)</li>
    </ul>

    <h3>AI-Powered Approach</h3>
    <ul>
      <li>AI detects risk factors → Alert generated → You reach out → Problem resolved → Client stays</li>
      <li>Time from risk detection to intervention: Same day</li>
      <li>Intervention window: Before client considers leaving</li>
    </ul>

    <h2>What the AI Analyzes</h2>

    <p>Pro IRP's AI examines multiple dimensions to calculate risk:</p>

    <h3>Engagement Signals</h3>
    <ul>
      <li>Contact recency: When did you last interact with this client?</li>
      <li>Response patterns: Are they returning calls and messages?</li>
      <li>Request patterns: Are they asking questions that suggest shopping?</li>
    </ul>

    <h3>Plan Intelligence</h3>
    <ul>
      <li>Premium changes: Is their cost increasing?</li>
      <li>Benefit changes: Are benefits being reduced?</li>
      <li>Network changes: Are their providers still in-network?</li>
      <li>Formulary changes: Are their medications still covered?</li>
      <li>Star rating trends: Is plan quality declining?</li>
    </ul>

    <h3>Client Factors</h3>
    <ul>
      <li>Tenure: First-year clients are higher risk</li>
      <li>Population type: Dual eligibles have different patterns</li>
      <li>Health indicators: Higher needs correlate with higher friction</li>
      <li>Past issues: Previous complaints predict future risk</li>
    </ul>

    <h3>Market Context</h3>
    <ul>
      <li>Competitor activity: What alternatives are being marketed?</li>
      <li>Seasonal factors: AEP proximity increases risk</li>
      <li>Regional patterns: Local market dynamics</li>
    </ul>

    <h2>Real Intelligence, Not Just Automation</h2>

    <p>There's a difference between automation and intelligence:</p>

    <p><strong>Basic automation:</strong> "Send an email to every client on their birthday."</p>

    <p><strong>AI intelligence:</strong> "This client's risk score jumped from 25 to 65 because their doctor left the network. They have an appointment with that doctor next month. Call them this week with information about in-network alternatives or transition options."</p>

    <p>Automation does the same thing for everyone. AI does the right thing for each individual client based on their specific situation.</p>

    <h2>How It Works in Practice</h2>

    <p>When you log into Pro IRP, the AI has already done the analysis:</p>

    <ul>
      <li><strong>Priority list:</strong> Clients ranked by risk, with the most urgent at the top</li>
      <li><strong>Action recommendations:</strong> Specific suggestions for what to do with each client</li>
      <li><strong>Alert notifications:</strong> New risks that emerged since your last login</li>
      <li><strong>Trend insights:</strong> Patterns across your book that need attention</li>
    </ul>

    <p>You're not starting your day wondering who to call. The AI tells you. You're not guessing why a client might be at risk. The AI explains. You're not hoping your outreach timing is right. The AI optimizes.</p>

    <h2>The Learning Loop</h2>

    <p>Every interaction creates data that improves the system:</p>

    <ul>
      <li>Client you intervened with stayed → Positive training signal</li>
      <li>Client predicted low-risk actually churned → Model adjustment</li>
      <li>Certain touchpoint types correlate with retention → Pattern recognition</li>
      <li>Your book's specific characteristics → Personalized learning</li>
    </ul>

    <p>The system doesn't just apply generic algorithms. It learns your book, your clients, your patterns. After a year of use, it knows your specific retention dynamics better than any general model could.</p>

    <h2>Who Benefits Most</h2>

    <p>AI-powered retention provides the most value for:</p>

    <ul>
      <li><strong>Growing books:</strong> More clients = more for AI to analyze and optimize</li>
      <li><strong>Complex populations:</strong> Dual eligibles, SNPs, clients with multiple products</li>
      <li><strong>Data-oriented agents:</strong> Those who want to make decisions based on evidence</li>
      <li><strong>Time-constrained agents:</strong> AI prioritization multiplies your limited time</li>
    </ul>

    <h2>The Competitive Edge</h2>

    <p>Your competitors are using the same basic tools everyone else has. The agents who pull ahead are the ones using intelligence that others don't have access to.</p>

    <p>When you know which clients are at risk before they do, when your outreach happens at the optimal moment, when your recommendations are based on data instead of intuition—you retain at a level that seems almost unfair to agents still doing it the old way.</p>

    <p>That's what AI-powered retention delivers. Not marginal improvement. Competitive advantage.</p>
  `,

  faqs: [
    {
      question: "How does AI predict which clients will churn?",
      answer: "Pro IRP's AI analyzes dozens of factors: contact recency, plan changes, network/formulary changes affecting the client, engagement patterns, client demographics, past issues, and market context. These factors combine into a risk score that predicts churn probability."
    },
    {
      question: "Does the AI get smarter over time?",
      answer: "Yes. The system continuously learns from outcomes in your specific book. Every retained client and every churned client provides training data. After months of use, the model is personalized to your book's unique patterns."
    },
    {
      question: "How is this different from basic CRM automation?",
      answer: "Automation does the same thing for everyone (send birthday emails). AI does the right thing for each individual based on their situation (call this specific client now because their doctor left the network and they have an appointment next month)."
    },
    {
      question: "What actions does the AI recommend?",
      answer: "Specific, contextual recommendations: who to call today, what to discuss, when to reach out, what problems to address. Not generic reminders—tailored guidance based on each client's risk factors and situation."
    },
    {
      question: "Do I need technical expertise to use AI retention tools?",
      answer: "No. Pro IRP presents AI insights through a simple interface: priority lists, action recommendations, and alerts. The complex analysis happens behind the scenes. You just see who needs attention and why."
    },
    {
      question: "What size book benefits from AI-powered retention?",
      answer: "AI provides value at any size, but becomes increasingly powerful as your book grows. With 100+ clients, you have enough data for meaningful pattern recognition. With 500+, the AI can find insights you'd never discover manually."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage in 2024: Enrollment Update and Key Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
